import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";

const QUALITY_FIELDS = [
  "category",
  "genre",
  "city",
  "venueName",
  "startTime",
  "priceRange",
  "artists",
  "tags",
  "images",
];

export function subscribeEventDiscoveryRows(onData, onError) {
  return onSnapshot(
    collection(db, "create_new_event"),
    (snapshot) => {
      const rows = snapshot.docs.map((entry) => normalizeEventDiscovery(entry));
      onData(rows);
    },
    (error) => {
      if (typeof onError === "function") onError(error);
    }
  );
}

export async function updateEventFeaturedVisibility(eventId, isFeatured, adminContext) {
  if (!eventId) throw new Error("Event id is required");

  const updates = {
    isFeatured: Boolean(isFeatured),
    updatedAt: serverTimestamp(),
    featuredUpdatedAt: serverTimestamp(),
  };

  const actor = serializeAdminContext(adminContext);
  if (actor) updates.featuredUpdatedBy = actor;

  await updateDoc(doc(db, "create_new_event", eventId), updates);
}

export async function updateEventVisibilityStatus(eventId, nextStatus, adminContext) {
  if (!eventId) throw new Error("Event id is required");

  const updates = {
    status: safeText(nextStatus, "draft").toLowerCase(),
    updatedAt: serverTimestamp(),
    visibilityReviewedAt: serverTimestamp(),
  };

  const actor = serializeAdminContext(adminContext);
  if (actor) updates.visibilityReviewedBy = actor;

  await updateDoc(doc(db, "create_new_event", eventId), updates);
}

export async function saveEventDiscoveryMetadata(eventId, values, referenceEvent, adminContext) {
  if (!eventId) throw new Error("Event id is required");

  const sanitized = sanitizeDiscoveryForm(values);
  const merged = {
    title: referenceEvent?.title,
    category: sanitized.category ?? referenceEvent?.category,
    eventGenre: sanitized.eventGenre ?? referenceEvent?.eventGenre,
    city: sanitized.city ?? referenceEvent?.city,
    venueName: sanitized.venueName ?? referenceEvent?.venueName,
    startLabel: referenceEvent?.startLabel,
    priceMin: sanitized.priceMin ?? referenceEvent?.priceMin,
    priceMax: sanitized.priceMax ?? referenceEvent?.priceMax,
    artistAssociations: referenceEvent?.artistAssociations,
    discoveryTags: sanitized.discoveryTags.length
      ? sanitized.discoveryTags
      : referenceEvent?.discoveryTags || [],
    imageCount: referenceEvent?.imageCount || 0,
  };

  const quality = calculateEventMetadataQuality(merged);

  const updates = {
    category: nullableText(sanitized.category),
    eventGenre: nullableText(sanitized.eventGenre),
    city: nullableText(sanitized.city),
    "schedule.venueName": nullableText(sanitized.venueName),
    type_of_event: nullableText(sanitized.typeOfEvent),
    discoveryTags: sanitized.discoveryTags,
    priceMin: sanitized.priceMin,
    priceMax: sanitized.priceMax,
    bookingCount: sanitized.bookingCount,
    viewCount: sanitized.viewCount,
    trendingScore: sanitized.trendingScore,
    discoveryQualityScore: quality.score,
    updatedAt: serverTimestamp(),
    discoveryMetadataReviewedAt: serverTimestamp(),
  };

  const actor = serializeAdminContext(adminContext);
  if (actor) updates.discoveryMetadataReviewedBy = actor;

  await updateDoc(doc(db, "create_new_event", eventId), updates);
}

export function normalizeEventDiscovery(entry) {
  const data = entry.data() || {};
  const title = safeText(
    data.name || data.about?.name || data.about?.title || data.title,
    "Untitled event"
  );
  const category = safeText(data.category || data.eventCategory);
  const eventGenre = safeText(data.eventGenre || data.genre);
  const typeOfEvent = safeText(data.type_of_event || data.eventType);
  const venueName = safeText(data.schedule?.venueName || data.venueName || data.venue?.name);
  const city = safeText(data.city || extractCity(data.schedule?.venueDetails || data.venueDetails));
  const description = safeText(data.about?.description || data.description);
  const organiserName = safeText(
    data.organiser?.name || data.organizerName || data.organiserName,
    "Unknown organiser"
  );
  const organiserId = safeText(
    data.organiser?.uid || data.organizerId || data.organiserId || data.organiser?.signupDocId
  );
  const organiserMobile = safeText(data.organiser?.mobile || data.organizerMobile);
  const durationMin = toNumber(data.additionalInfo?.durationMin || data.durationMin);
  const schedule = deriveScheduleSummary(data.schedule?.shows, durationMin, data.startTime, data.endTime);
  const ticketMetrics = deriveTicketMetrics(data.tickets, data.priceMin, data.priceMax, data.bookingCount);
  const viewCount = toNumber(data.viewCount || data.views || data.seen || data.impressions);
  const images = normalizeImages(data.images);
  const artists = normalizeArtists(data.artists?.selected || data.artistIds || data.artistAssociations);
  const discoveryTags = normalizeStringArray(data.discoveryTags || data.tags || data.keywords);
  const trendingScore =
    nullableNumber(data.trendingScore) ??
    computeTrendingScore({
      bookingCount: ticketMetrics.bookingCount,
      viewCount,
      isFeatured: Boolean(data.isFeatured),
      startMs: schedule.startMs,
    });
  const visibilityStatus = safeText(data.status, "draft").toLowerCase();
  const quality =
    nullableNumber(data.discoveryQualityScore) != null
      ? {
          score: clamp(nullableNumber(data.discoveryQualityScore), 0, 100),
          missing: calculateEventMetadataQuality({
            category,
            eventGenre,
            city,
            venueName,
            startLabel: schedule.startLabel,
            priceMin: ticketMetrics.priceMin,
            priceMax: ticketMetrics.priceMax,
            artistAssociations: artists,
            discoveryTags,
            imageCount: images.length,
          }).missing,
        }
      : calculateEventMetadataQuality({
          category,
          eventGenre,
          city,
          venueName,
          startLabel: schedule.startLabel,
          priceMin: ticketMetrics.priceMin,
          priceMax: ticketMetrics.priceMax,
          artistAssociations: artists,
          discoveryTags,
          imageCount: images.length,
        });

  const createdAt = data.createdAt || data.submittedAt || null;
  const updatedAt = data.updatedAt || null;

  return {
    id: entry.id,
    raw: data,
    title,
    category,
    categoryLabel: category || "Uncategorized",
    eventGenre,
    genreLabel: eventGenre || "Not tagged",
    typeOfEvent,
    city,
    cityLabel: city || "Unknown city",
    venueName,
    venueLabel: venueName || "Venue missing",
    description,
    organiserName,
    organiserId,
    organiserMobile,
    startLabel: schedule.startLabel,
    endLabel: schedule.endLabel,
    startMs: schedule.startMs,
    endMs: schedule.endMs,
    startTimeRaw: schedule.startRaw,
    endTimeRaw: schedule.endRaw,
    priceMin: ticketMetrics.priceMin,
    priceMax: ticketMetrics.priceMax,
    priceRangeLabel: formatPriceRange(ticketMetrics.priceMin, ticketMetrics.priceMax),
    bookingCount: ticketMetrics.bookingCount,
    viewCount,
    trendingScore,
    visibilityStatus,
    visibilityLabel: formatLabel(visibilityStatus),
    isFeatured: Boolean(data.isFeatured),
    discoveryTags,
    images,
    imageCount: images.length,
    artistAssociations: artists,
    artistCount: artists.length,
    languages: normalizeStringArray(data.additionalInfo?.languages),
    ageGroups: normalizeStringArray(data.additionalInfo?.ageGroups),
    durationMin,
    missingMetadataFields: quality.missing,
    discoveryQualityScore: quality.score,
    createdAt,
    updatedAt,
    createdAtMs: toMillis(createdAt),
    updatedAtMs: toMillis(updatedAt),
    searchText: [
      entry.id,
      title,
      category,
      eventGenre,
      typeOfEvent,
      city,
      venueName,
      organiserName,
      organiserId,
      artists.map((artist) => `${artist.name} ${artist.categoryLabel}`).join(" "),
      discoveryTags.join(" "),
      visibilityStatus,
    ]
      .join(" ")
      .toLowerCase(),
  };
}

export function calculateEventMetadataQuality(source) {
  const presence = {
    category: Boolean(safeText(source?.category)),
    genre: Boolean(safeText(source?.eventGenre || source?.genre)),
    city: Boolean(safeText(source?.city)),
    venueName: Boolean(safeText(source?.venueName)),
    startTime: Boolean(safeText(source?.startLabel || source?.startTime)),
    priceRange:
      nullableNumber(source?.priceMin) != null || nullableNumber(source?.priceMax) != null,
    artists:
      Array.isArray(source?.artistAssociations)
        ? source.artistAssociations.length > 0
        : normalizeStringArray(source?.artistIds).length > 0,
    tags: normalizeStringArray(source?.discoveryTags || source?.tags).length > 0,
    images: Number(source?.imageCount || 0) > 0,
  };

  const completed = QUALITY_FIELDS.filter((key) => presence[key]).length;
  const score = Math.round((completed / QUALITY_FIELDS.length) * 100);
  const missing = QUALITY_FIELDS.filter((key) => !presence[key]);

  return { score, missing };
}

function sanitizeDiscoveryForm(values) {
  return {
    category: normalizeOptionalText(values?.category),
    eventGenre: normalizeOptionalText(values?.eventGenre),
    city: normalizeOptionalText(values?.city),
    venueName: normalizeOptionalText(values?.venueName),
    typeOfEvent: normalizeOptionalText(values?.typeOfEvent),
    discoveryTags: normalizeStringArray(values?.discoveryTags),
    priceMin: nullableNumber(values?.priceMin),
    priceMax: nullableNumber(values?.priceMax),
    bookingCount: nullableNumber(values?.bookingCount),
    viewCount: nullableNumber(values?.viewCount),
    trendingScore: nullableNumber(values?.trendingScore),
  };
}

function deriveScheduleSummary(shows, durationMin, startValue, endValue) {
  const directStart = toMillis(startValue);
  const directEnd = toMillis(endValue);

  if (directStart) {
    return {
      startMs: directStart,
      endMs: directEnd || (durationMin > 0 ? directStart + durationMin * 60000 : directStart),
      startLabel: formatDateTime(startValue),
      endLabel: directEnd ? formatDateTime(endValue) : "",
      startRaw: startValue || null,
      endRaw: endValue || null,
    };
  }

  const instants = [];
  if (Array.isArray(shows)) {
    shows.forEach((row) => {
      const date = safeText(row?.date || row?.day || row?.startDate);
      const times = normalizeStringArray(row?.times || row?.time || row?.slots);
      if (!date) return;
      if (!times.length) {
        const justDate = new Date(date).getTime();
        if (Number.isFinite(justDate)) instants.push(justDate);
        return;
      }

      times.forEach((time) => {
        const stamp = parseDateTime(date, time);
        if (stamp) instants.push(stamp);
      });
    });
  }

  instants.sort((left, right) => left - right);
  const startMs = instants[0] || 0;
  const latestStart = instants[instants.length - 1] || 0;
  const endMs =
    latestStart && durationMin > 0 ? latestStart + durationMin * 60000 : latestStart || startMs;

  return {
    startMs,
    endMs,
    startLabel: startMs ? formatDateTime(new Date(startMs)) : "Schedule missing",
    endLabel: endMs ? formatDateTime(new Date(endMs)) : "",
    startRaw: startMs ? new Date(startMs).toISOString() : null,
    endRaw: endMs ? new Date(endMs).toISOString() : null,
  };
}

function deriveTicketMetrics(tickets, storedMin, storedMax, storedBookings) {
  const rows = Array.isArray(tickets) ? tickets : [];
  const prices = rows
    .map((ticket) => nullableNumber(ticket?.price))
    .filter((amount) => amount != null);

  const sold = rows.reduce((sum, ticket) => sum + toNumber(ticket?.sold), 0);
  const priceMin = nullableNumber(storedMin) ?? (prices.length ? Math.min(...prices) : null);
  const priceMax = nullableNumber(storedMax) ?? (prices.length ? Math.max(...prices) : null);
  const bookingCount = nullableNumber(storedBookings) ?? sold;

  return { priceMin, priceMax, bookingCount };
}

function normalizeArtists(value) {
  if (!value) return [];

  const list = Array.isArray(value) ? value : [value];

  return list
    .map((item, index) => {
      if (item == null) return null;
      if (typeof item === "string") {
        const text = safeText(item);
        return text
          ? {
              id: text,
              name: text,
              categoryLabel: "",
            }
          : null;
      }

      const id = safeText(item.id || item.artistId || item.uid, `artist-${index}`);
      const name = safeText(item.name || item.artistName || item.stageName, `Artist ${index + 1}`);
      const categories = normalizeStringArray(item.categories || item.artistCategories || item.category);
      return {
        id,
        name,
        categoryLabel: categories.join(", "),
      };
    })
    .filter(Boolean);
}

function normalizeImages(value) {
  if (!value || typeof value !== "object") return [];

  const candidates = [
    value.horizontalBanner?.url,
    value.verticalPoster?.url,
    value.headerImageUrl,
    value.verticalPosterUrl,
  ];

  return candidates.map((item) => safeText(item)).filter(Boolean);
}

function normalizeStringArray(value) {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((item) => safeText(item))
          .filter(Boolean)
      )
    );
  }

  const text = safeText(value);
  if (!text) return [];

  return Array.from(
    new Set(
      text
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

function extractCity(venueDetails) {
  if (!venueDetails) return "";
  const city = safeText(venueDetails.city || venueDetails.town || venueDetails.locality);
  if (city) return city;

  const address = safeText(venueDetails.address || venueDetails.label);
  if (!address) return "";

  const parts = address
    .split(",")
    .map((item) => item.replace(/\b\d{5,6}\b/g, "").trim())
    .filter(Boolean);

  if (parts.length >= 3) return parts[parts.length - 3];
  if (parts.length >= 2) return parts[parts.length - 2];
  return parts[0] || "";
}

function computeTrendingScore({ bookingCount = 0, viewCount = 0, isFeatured = false, startMs = 0 }) {
  const bookings = toNumber(bookingCount);
  const views = toNumber(viewCount);
  const now = Date.now();
  const daysUntilStart = startMs ? Math.max(0, Math.round((startMs - now) / 86400000)) : 999;
  const freshnessBoost = daysUntilStart <= 14 ? 12 : daysUntilStart <= 30 ? 6 : 0;

  return Math.round(
    clamp(bookings * 4 + Math.min(40, views / 25) + freshnessBoost + (isFeatured ? 10 : 0), 0, 100)
  );
}

function parseDateTime(date, time) {
  const normalizedDate = safeText(date);
  const normalizedTime = safeText(time);
  if (!normalizedDate) return 0;

  const candidate = normalizedTime ? `${normalizedDate}T${normalizedTime}` : normalizedDate;
  const stamp = new Date(candidate).getTime();
  return Number.isFinite(stamp) ? stamp : 0;
}

function formatDateTime(value) {
  const date = toDate(value);
  if (!date) return "";

  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

function formatPriceRange(min, max) {
  const low = nullableNumber(min);
  const high = nullableNumber(max);

  if (low != null && high != null) {
    return `Rs ${formatMoney(low)} - Rs ${formatMoney(high)}`;
  }
  if (high != null) return `Up to Rs ${formatMoney(high)}`;
  if (low != null) return `From Rs ${formatMoney(low)}`;
  return "Not shared";
}

function formatMoney(amount) {
  try {
    return new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));
  } catch {
    return String(amount || 0);
  }
}

function formatLabel(value) {
  return String(value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase())
    .trim();
}

function safeText(value, fallback = "") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function normalizeOptionalText(value) {
  const text = safeText(value);
  return text || null;
}

function nullableText(value) {
  return value == null ? null : safeText(value) || null;
}

function nullableNumber(value) {
  if (value === "" || value == null) return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

function toNumber(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const stamp = new Date(value).getTime();
  return Number.isFinite(stamp) ? stamp : 0;
}

function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, Number(value || 0)));
}

function serializeAdminContext(adminContext) {
  const docId = safeText(adminContext?.docId);
  const name = safeText(adminContext?.name);
  const role = safeText(adminContext?.role);

  if (!docId && !name && !role) return null;

  return {
    ...(docId ? { docId } : {}),
    ...(name ? { name } : {}),
    ...(role ? { role } : {}),
  };
}
